import ms from './enums/system.js';
import ConversionResult from './entities/conversionResult.js';
import ConversionError from './entities/conversionError.js';
import CompositeResultFormatter from './formatters/compositeResultFormatter.js';
import Quantity from './entities/quantity.js';

"use strict";

class UnitsConverter {
	//static VERSION = '3.2.0';

	constructor(configuration) {
		this.DEBUG = false;
		this.targetSystem  = null;
		this.baseSystem = ms.SI;
		this.systems = [],//[ms.IMPERIAL, ms.METRIC, ms.SI, ms.HYBRID, ms.CUSTOM, ms.EUROPEAN];
		this.types = {};
		this.quantities = {};

		this.lastConversion = null;

		if (typeof configuration === 'object'){
			this.autoConfigure(configuration);
		}
	}

	log (message) {
		if (this.DEBUG) {
			console.debug(message);
		}
	}

	autoConfigure (params) {
		if( params.hasOwnProperty( 'systems' ) ){
			if (!(params.systems instanceof Array)) {
				throw new TypeError(`systems property must contain an array`);
			}
				
			this.systems = params.systems;
		}
		if( params.hasOwnProperty( 'types' ) ){
			if (!(params.types instanceof Object)) {
				throw new TypeError(`types property must contain an object`);
			}

			this.types = params.types;
		}
		if( params.hasOwnProperty( 'quantities' ) ){
			if (!(params.quantities instanceof Object)) {
				throw new TypeError(`quantities property must contain an object`);
			}

			this.quantities = params.quantities;
		}
		if( params.hasOwnProperty( 'targetSystem' ) ){
			if (typeof params.targetSystem !== 'string') {
				throw new TypeError(`targetSystem property must contain an string`);
			}

			this.targetSystem  = params.targetSystem;
		}
		if( params.hasOwnProperty( 'baseSystem' ) ){
			if (typeof params.baseSystem !== 'string') {
				throw new TypeError(`baseSystem property must contain an string`);
			}
			this.baseSystem  = params.baseSystem;
		}
	}

	/**
	 * Allows to register new quantity or replace current definition for given quantityId
	 * @param {Quantity} definition - quantity definition
	 * @param {Boolean} override - if quantity is registered, should be overridden then ? default false
	 */
	registerQuantity (definition, override){
        if (!(definition instanceof Quantity)){
            throw new TypeError('definition must be instance of Quantity class');
        }

        const shouldOverride = !!override;

        if (this.quantities.hasOwnProperty(definition.id) && !shouldOverride){
            throw new Error(`quantity with id ${definition.id} cannot be re-defined`)
        }

		this.quantities[definition.id] = definition;
	}

	/**
	 * Allows to register new conversion type or replace current definition for given typeId
	 * @param {String} typeId - id of quantity
	 * @param {Object} definition - quantity definition
	 */
	registerType (typeId, definition){
		this.types[typeId] = definition;
	}

	/**
	 * Method that returns converted value
	 *
	 * @param {Number} value - value to convert
	 * @param {String} quantityTypeId - type of quantity to convert: length, area, volume...
	 * @param {String} baseUnit - base unit of value to convert
	 * @param {String} targetUnit - unit after conversion
	 * @returns {ConversionResult|ConversionError}
	 */
	convert (value, quantityTypeId, baseUnit, targetUnit) {
		let conversionError;
		try {
			if (typeof (value) === 'undefined' || !UnitsConverter.isNumeric(value)) {
				conversionError = new TypeError('Value passed for conversion is not an number');
			}
			else if (typeof (quantityTypeId) !== 'string') {
				conversionError = new TypeError('No type used for conversion');
			}
			else{
				let fromUnit, toUnit;
				const quantity = this.getQuantity(quantityTypeId);
				if (!quantity) {
					conversionError = new TypeError(`Unit definition for ${quantityTypeId} is not defined`);
				}
				else{
					fromUnit = quantity.getUnit(baseUnit);
					toUnit = quantity.getUnit(targetUnit);
		
					if (!fromUnit) {
						conversionError = new TypeError(`Unit factors for ${baseUnit} system is not defined for type ${quantityTypeId}`);
					}
					else if (!toUnit) {
						conversionError = new TypeError(`Unit factors for ${targetUnit} system is not defined for type ${quantityTypeId}`);
					}
					else{
						let converted;
						converted = toUnit.convertFromBase(fromUnit.convertToBase(value));

						if( typeof(converted) !== "undefined" && UnitsConverter.isNumeric(converted) || UnitsConverter.isString(converted) ) {
							let convResult = new ConversionResult(value, converted, quantity, fromUnit, toUnit);
							if (this.DEBUG){
								this.lastConversion = convResult;
							}

							return convResult;
						}

						conversionError = new Error("Conversion for given parameters failed");
					}
				}
			}
		} catch (e) {
			conversionError = e;
			this.log(e);
		}

		let convError = new ConversionError(value, conversionError, quantityTypeId, baseUnit, targetUnit);
		if (this.DEBUG){
			this.lastConversion = convError;
		}

		return convError;
	}

	/**
	 * Method that returns converted value for specific type
	 *
	 * @param {Number} value - value to convert
	 * @param {String} conversionTypeId - type of quantity to convert: length, area, volume...
	 * @param {String} targetSystem - optional, target system for conversion, can override this.targetSystem
	 * @returns {ConversionResult|ConversionError}
	 */
	convertType (value, conversionTypeId, targetSystem) {
		let conversionError;
		try {
			if (typeof (value) === 'undefined' || !UnitsConverter.isNumeric(value)) {
				conversionError = new TypeError('Value passed for conversion is not an number');
			}
			else if (typeof (conversionTypeId) !== 'string') {
				conversionError = new TypeError('No type used for conversion');
			}
			else{
				const convType = this.getType(conversionTypeId);
				if (!convType) {
					conversionError = new TypeError(`Conversion type definition for ${conversionTypeId} does not exist`);
				}
				else{
					const baseUnit = convType.set[this.baseSystem];
					const targetUnit = convType.set[targetSystem || this.targetSystem];
		
					if (!baseUnit) {
						conversionError = new TypeError(`Unit for ${this.baseSystem} system is not defined for type ${conversionTypeId}`);
					}
					else if (!targetUnit) {
						conversionError = new TypeError(`Unit for ${targetSystem || this.targetSystem} system is not defined for type ${conversionTypeId}`);
					}
					else{
						let conversionResult = this.convert(value, convType.quantityId, baseUnit, targetUnit);
						if (convType.hasOwnProperty('formatter')){
							// todo - not the best way to do it, there should be some formatter per type provider...
							let targetUnitCopy = Object.assign( Object.create( Object.getPrototypeOf(conversionResult.targetUnit)), conversionResult.targetUnit);//Object.assign({}, conversionResult.targetUnit);
							let formatter = convType.formatter;
							if (formatter && targetUnitCopy.formatter){
								targetUnitCopy.setFormatter(new CompositeResultFormatter(
									targetUnitCopy.formatter, convType.formatter
								));
							}
							else if (formatter){
								targetUnitCopy.formatter = formatter;
							}
							
							conversionResult.targetUnit = targetUnitCopy;
						}

						return conversionResult
					}
				}
			}
		} catch (e) {
			conversionError = e;
			this.log(e);
		}

		let convError = new ConversionError(value, conversionError, conversionTypeId, undefined, undefined);
		if (this.DEBUG){
			this.lastConversion = convError;
		}

		return convError;
	}

	getQuantity(quantityId) {
		try {
			if (quantityId && this.quantities.hasOwnProperty(quantityId)) {
				let quantity = this.quantities[quantityId];

				if (quantity) {
					return quantity;
				}
			}

			throw new TypeError(`Quantity ${quantityId} doesn't exist in registry of quantities.`);
		} catch (e) {
			this.log(e);
		}

		return false;
	}

	getType(typeId) {
		try {
			if (!typeId || !this.types.hasOwnProperty(typeId)) {
				throw new TypeError(`Type ${typeId} doesn't exist in registry of conversion types.`);
			}

			let subtype = this.types[typeId];
			let quantityType = subtype.quantityId;

			if (!quantityType || !this.quantities.hasOwnProperty(quantityType)) {
				throw new TypeError(`Quantity ${quantityType} for conversion ${typeId} doesn't exist in registry of quantities.`);
			}

			return subtype;
		} catch (e) {
			this.log(e);
		}

		return false;
	}

	getUnitForType( typeId, targetSystem ) {
		try {
			if (typeof typeId !== 'string' || !this.types.hasOwnProperty(typeId)){
				throw new TypeError(`type must contain string and be registered in converter`);
			}

			if (typeof targetSystem !== 'string' || !targetSystem){
				throw new TypeError(`targetSystem must contain non empty string`);
			}

			let conversionType = this.getType(typeId);
			if (!conversionType){
				throw new TypeError(`unable to find type registered under id ${typeId}`);
			}

			let quantity = this.getQuantity(conversionType.quantityId);
			if (!quantity){
				throw new TypeError(`unable to find type quantity for type ${typeId}`);
			}

			let unitId = conversionType.set[targetSystem];
			if (!unitId){
				throw new TypeError(`unable to find unit assigned for untis system ${`targetSystem`} in type ${typeId}`);
			}

			let unit = quantity.getUnit(unitId);
			if (!unit) {
				throw new TypeError(`Quantity doesn't contain definition for unit ${unitId}`);
			}

			return unit;
			
		} catch (e) {
			this.log(e);
		}

		return false;
	}

	static isNumeric( obj ) {
		// parseFloat NaNs numeric-cast false positives (null|true|false|"")
		// ...but misinterprets leading-number strings, particularly hex literals ("0x...")
		// subtraction forces infinities to NaN
		// adding 1 corrects loss of precision from parseFloat (#15100)
		return !obj.isArray && (obj - parseFloat( obj ) + 1) >= 0;
	}

	static isString( obj ) {
		return !UnitsConverter.isNumeric(obj) && 'string' === typeof obj;
	}
}

export default UnitsConverter;